import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import api from '../../services/api';

const isStepUp = (err) => {
  const structured = err.response?.data?.detail_object;
  return err.response?.status === 403 && structured?.error === 'mfa_step_up_required';
};

/**
 * Explicit, audited enrollment of selected patients into (or out of) the
 * automated daily 270 sweep. Import/creation never enrolls — this is the
 * ONLY promotion path (Agency Admin + fresh MFA, server-enforced).
 */
export function useEligibilityAutomation({ selectedIds, onDone }) {
  const [pending, setPending] = useState(null); // { enrolled: bool } | null
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [stepUpOpen, setStepUpOpen] = useState(false);

  const open = (enrolled) => { setReason(''); setPending({ enrolled }); };
  const close = () => setPending(null);

  const submit = useCallback(async () => {
    if (!pending || !selectedIds.length) return;
    setSubmitting(true);
    try {
      const res = await api.post('/patients/eligibility-automation', {
        patient_ids: selectedIds, enrolled: pending.enrolled, reason: reason.trim(),
      });
      const { updated, not_found_in_agency = [], warning } = res.data;
      toast.success(pending.enrolled
        ? `${updated} patient${updated === 1 ? '' : 's'} enrolled in daily 270 automation`
        : `${updated} patient${updated === 1 ? '' : 's'} removed from daily 270 automation`);
      if (not_found_in_agency.length && warning) toast.warning(warning);
      setPending(null);
      onDone();
    } catch (err) {
      if (isStepUp(err)) {
        setStepUpOpen(true);
      } else {
        const d = err.response?.data?.detail_object;
        toast.error((typeof d === 'object' && d?.message) || err.response?.data?.detail || 'Enrollment change failed');
      }
    } finally {
      setSubmitting(false);
    }
  }, [pending, selectedIds, reason, onDone]);

  return { pending, reason, setReason, submitting, stepUpOpen, setStepUpOpen, open, close, submit };
}
